import React, { useState, useEffect } from 'react';
import {
  User, Calendar, Tag, Layers, MapPin, List,
  Image, Video, Info, Clock, Send, X,
  Paperclip, LayoutGrid
} from 'lucide-react';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import Select from 'react-select';
import { fireAudit } from '../utils/auditClient';

const UserAvatar = ({ name, userId }) => {
  const getInitials = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.split(' ');
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
    return (parts[0][0] || '') + (parts[parts.length - 1][0] || '');
  };
  const initials = getInitials(name);
  const bgColorClass = name === userId ? 'ts-avatar-bg-blue' : 'ts-avatar-bg-gray';
  return (
    <div className={`ts-user-avatar ${bgColorClass}`}>
      {initials.toUpperCase()}
    </div>
  );
};

const TicketDetail = ({
  ticket,
  onClose,
  setTickets,
  userId,
  getPriorityClass,
  getStatusClass,
  getStatusIcon,
  formatDate,
  updateTicketStatus,
  categories = [],
  refresh,
  users,
  reassignTickets,
  priorities,
  updateTicketPriority
}) => {
  const [newComment, setNewComment] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [showAllTimeline, setShowAllTimeline] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mediaPreview, setMediaPreview] = useState({ type: null, url: null });
  const [selectedStatus, setSelectedStatus] = useState(ticket.status?.trim().toUpperCase());
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const { userData } = useUser();
  useEffect(() => {
    setNewComment('');
    setCommentAttachments([]);
    setShowAllTimeline(false);
    setMediaPreview({ type: null, url: null });
    setSelectedStatus(ticket.status);
  }, [ticket?.id]);

  const getStatusOptions = () => {
    if (!ticket?.category || !Array.isArray(categories)) return [];

    const selectedCat = categories.find(cat => cat.name === ticket.category);
    if (!selectedCat || !Array.isArray(selectedCat.statuses)) return [];

    return selectedCat.statuses.map((statusObj) => {
      const label = Object.values(statusObj)[0];
      const value = label?.toUpperCase();
      return {
        value,
        label,
        icon: getStatusIcon(value)
      };
    });
  };
  const statusOptions = getStatusOptions();

  const formatOptionLabel = ({ value, label, icon }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {icon}
      {label}
    </div>
  );
  const removeAttachment = (idx) => {
    setCommentAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const allowed = [];
    files.forEach(file => {
      allowed.push(file);
    });
    setCommentAttachments(prev => [...prev, ...allowed]);
    e.target.value = null;
  };
  const combinedTimeline = [
    ...(ticket.comments || []).map(c => ({ ...c, type: 'comment', timestamp: c.timestamp, author: c.author })),
    ...(ticket.activity || []).map(a => ({ ...a, timestamp: a.timestamp, user: a.user }))
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const addComment = async (ticketId) => {
    if (!newComment.trim() && commentAttachments.length === 0) return;
    if (submitting) return;

    setSubmitting(true);
    const formData = new FormData();
    formData.append('ids', ticketId);
    formData.append('comment', newComment);
    formData.append('performedBy', userId);
    commentAttachments.forEach((file) => {
      if (file.type.startsWith('image')) {
        formData.append('photos', file);
      } else if (file.type.startsWith('video')) {
        formData.append('videos', file);
      }
    });

    try {
      const response = await fetch(process.env.REACT_APP_API_ADD_COMMENTS, {
        method: 'POST',
        headers: {
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Network response was not ok');
      await response.json();

      await fireAudit({
        entity: 'ticket_comment',
        affected_id: String(ticketId),
        action: 'create',
        section: 'Tickets',
        subSection: 'Comment',
        businessApi: process.env.REACT_APP_API_ADD_COMMENTS,
        businessMethod: 'POST',
        oldPayload: {},
        newPayload: { ids: ticketId, comment: newComment, performedBy: userId, attachments: commentAttachments.map(f => f.name) }
      });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: 'Comment added successfully!',
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      setNewComment('');
      setCommentAttachments([]);
      if (refresh) refresh();
      setSubmitting(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to add comment!',
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false
      });
      setSubmitting(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (comment) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this comment?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const url = `${process.env.REACT_APP_API_DELETE_COMMENT}?ids=${comment.id}`;
          const headers = {
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          };
          const response = await fetch(url, {
            method: 'POST',
            headers,
          });
          if (!response.ok) throw new Error("Failed to delete comment!");

          await fireAudit({
            entity: 'ticket_comment',
            affected_id: String(comment.id),
            action: 'delete',
            section: 'Tickets',
            subSection: 'Comment',
            businessApi: process.env.REACT_APP_API_DELETE_COMMENT,
            businessMethod: 'POST',
            oldPayload: { id: comment.id, text: comment.text },
            newPayload: { ids: comment.id }
          });

          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Comment deleted.",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
          });

          if (refresh) refresh();

        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.message || 'Failed to delete comment!',
            toast: true,
            position: "top-end",
            timer: 3000,
            showConfirmButton: false
          });
        }
      }
    });
  };

  const handleStatusChange = async (newStatus) => {
    if (!hasPrivilege(userData, 'TICKET', 'TICKETS_STATUS_MODIFY')) {
      Swal.fire({
        icon: 'error',
        title: 'Permission Denied',
        text: 'You do not have permission to change ticket status',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    setIsChangingStatus(true);
    try {
      await updateTicketStatus([ticket.id], newStatus);

      Swal.fire({
        icon: 'success',
        title: 'Status Updated!',
        text: `Ticket status changed to ${newStatus}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500
      });

      setSelectedStatus(newStatus);
      if (refresh) refresh();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update status',
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsChangingStatus(false);
      setShowStatusDropdown(false);
    }
  };

  const timelineToShow = showAllTimeline ? combinedTimeline : combinedTimeline.slice(0, 3);
  const displayAttachments = (ticket.attachments || []).map(att => {
    if (att.photoUrl) return { type: 'image', url: att.photoUrl };
    if (att.videoUrl) return { type: 'video', url: att.videoUrl };
    return null;
  }).filter(Boolean);

  const [isAssigning, setIsAssigning] = useState(false);
  const initialAssignees = ticket.assignedTos?.map(a => ({
    label: a.name,
    value: a.id
  })) || [];
  const [selectedAssignee, setSelectedAssignee] = useState(initialAssignees);
  const [selectedPriority, setSelectedPriority] = useState(ticket.priority);
  const [isChangingPriority, setIsChangingPriority] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  useEffect(() => {
    setSelectedPriority(ticket.priority);
  }, [ticket.id, ticket.priority]);
  const priorityOptions = (priorities || []).map(p => ({
    value: p,
    label: p,
    className: getPriorityClass(p)
  }));

  return (
    <>
      {mediaPreview.url && (
        <div
          className="ts-lightbox-overlay"
          onClick={() => setMediaPreview({ type: null, url: null })}
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.85)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setMediaPreview({ type: null, url: null })}
              style={{
                position: "absolute",
                top: 10, right: 10,
                background: "rgba(0,0,0,0.5)",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 32, height: 32,
                fontSize: 20,
                cursor: "pointer",
                zIndex: 10
              }}
            >×</button>
            {mediaPreview.type === "image" ? (
              <img
                src={mediaPreview.url}
                alt="Preview"
                style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8, boxShadow: "0 4px 24px #0008" }}
              />
            ) : (
              <video
                src={mediaPreview.url}
                controls
                autoPlay
                style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8, background: "#222", boxShadow: "0 4px 24px #0008" }}
              />
            )}
          </div>
        </div>
      )}

      <div className="ts-ticket-detail-overlay" onClick={onClose}>
        <div className="ts-ticket-detail-content" onClick={e => e.stopPropagation()}>
          <div className="ts-ticket-detail-header">
            <h2 className="ts-ticket-detail-title" style={{ textAlign: 'center' }}>{ticket.title}</h2>
            <button onClick={onClose} className="ts-ticket-detail-close-button">x</button>
          </div>
          <div className="ts-ticket-detail-body">
            <div className="ts-ticket-tags-container">
              <span className="ts-ticket-tag ts-ticket-tag-default">
                <MapPin className="ts-icon-x-small ts-icon-inline ts-icon-margin-right" /> {ticket.centre}
              </span>
              <span className="ts-ticket-tag ts-ticket-tag-indigo">
                <Layers className="ts-icon-x-small ts-icon-inline ts-icon-margin-right" /> {ticket.category}
              </span>
              <span className="ts-ticket-tag ts-ticket-tag-teal">
                <LayoutGrid className="ts-icon-x-small ts-icon-inline ts-icon-margin-right" /> {ticket.subcategory}
              </span>
              <span className={`ts-ticket-tag ${getStatusClass(ticket.status)}`}>
                {getStatusIcon(ticket.status)} {ticket.status}
              </span>
            </div>
            <div className="ts-ticket-info-grid">
              <div><strong className="ts-ticket-info-label"><User className="ts-icon-x-small ts-icon-margin-right" /> Created by:</strong> {ticket.createdBy}</div>
              <div><strong className="ts-ticket-info-label"><User className="ts-icon-x-small ts-icon-margin-right" /> Assigned to:</strong> {ticket.assignedTo}</div>
              <div><strong className="ts-ticket-info-label"><Calendar className="ts-icon-x-small ts-icon-margin-right" /> Created:</strong> {formatDate(ticket.createdAt)}</div>
              <div><strong className="ts-ticket-info-label"><Calendar className="ts-icon-x-small ts-icon-margin-right" /> Last Updated:</strong> {formatDate(ticket.updatedAt)}</div>
            </div>
            <div className="ts-ticket-description-section">
              <h3 className="ts-section-title"><List className="ts-icon-small ts-icon-margin-right" /> Description</h3>
              <p className="ts-ticket-description-text">{ticket.description}</p>
              {displayAttachments.length > 0 && (
                <div className="ts-attachment-section">
                  <h4 className="ts-attachment-title"><Info className="ts-icon-x-small ts-icon-margin-right" /> Attachments</h4>
                  <div className="ts-attachment-grid">
                    {displayAttachments.map((attachment, idx) => (
                      <div key={idx} className="ts-attachment-card">
                        {attachment.type === 'image' && (
                          <img
                            src={attachment.url}
                            alt={`Attachment ${idx + 1}`}
                            className="ts-attachment-media"
                            style={{ cursor: 'zoom-in' }}
                            onClick={() => setMediaPreview({ type: 'image', url: attachment.url })}
                            onError={e => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x250/E2E8F0/64748B?text=Image+Error'; }}
                          />
                        )}
                        {attachment.type === 'video' && (
                          <video
                            controls
                            className="ts-attachment-media"
                            style={{ cursor: 'zoom-in' }}
                            onClick={() => setMediaPreview({ type: 'video', url: attachment.url })}
                          >
                            <source src={attachment.url} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        )}
                        <div className="ts-attachment-label">
                          {attachment.type === 'image' ? <Image className="ts-icon-x-small" /> : <Video className="ts-icon-x-small" />}
                          {attachment.type === 'image' ? 'Image' : 'Video'} Attachment
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {hasPrivilege(userData, 'TICKET', 'TICKETS_STATUS_MODIFY') && (
              <div className="ts-status-selector-section">
                <h3 className="ts-section-title">
                  <Tag className="ts-icon-small ts-icon-margin-right" />
                  Ticket Status
                </h3>

                <div className="ts-status-selector-container">
                  {statusOptions.length > 0 ? (
                    <>
                      <Select
                        className="ts-react-select-container"
                        classNamePrefix="ts-react-select"
                        options={statusOptions}
                        placeholder="Status"
                        isClearable={false}
                        isSearchable={false}
                        value={statusOptions.find(opt => opt.value === selectedStatus)}
                        onChange={(selectedOption) => {
                          setSelectedStatus(selectedOption?.value);
                        }}
                        menuPortalTarget={document.body}
                        formatOptionLabel={formatOptionLabel}
                        styles={{
                          placeholder: (provided) => ({
                            ...provided,
                            color: 'black',
                          }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        isDisabled={isChangingStatus || !hasPrivilege(userData, 'TICKET', 'TICKETS_STATUS_MODIFY')}
                      />

                      {selectedStatus !== ticket.status?.trim().toUpperCase() && hasPrivilege(userData, 'TICKET', 'TICKETS_STATUS_MODIFY') && (
                        <button
                          onClick={() => handleStatusChange(selectedStatus)}
                          className="ts-status-update-button"
                          style={{
                            marginTop: '10px',
                            padding: '6px 14px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                          disabled={isChangingStatus}
                        >
                          Update Status
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="ts-no-status-options">
                      No status options available for this category
                    </div>
                  )}
                  {isChangingStatus && (
                    <div className="ts-status-updating-indicator">
                      Updating status...
                    </div>
                  )}
                </div>
              </div>
            )}
            {hasPrivilege(userData, 'TICKET', 'TICKETS_PRIORITY_MODIFY') && (
            <div className="ts-status-selector-section">
              <h3 className="ts-section-title">
                <Tag className="ts-icon-small ts-icon-margin-right" />
                Ticket Priority
              </h3>
              <div className="ts-status-selector-container">
                <Select
                  className="ts-react-select-container"
                  classNamePrefix="ts-react-select"
                  options={priorityOptions}
                  placeholder="Priority"
                  isClearable={false}
                  isSearchable={false}
                  value={priorityOptions.find(opt => opt.value === selectedPriority)}
                  onChange={opt => setSelectedPriority(opt.value)}
                  menuPortalTarget={document.body}
                  styles={{
                    placeholder: provided => ({ ...provided, color: 'black' }),
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                  }}
                  isDisabled={isChangingPriority}
                />
                {selectedPriority !== ticket.priority && (
                  <button
                    onClick={async () => {
                      setIsChangingPriority(true);
                      try {
                        await updateTicketPriority([ticket.id], selectedPriority);
                        Swal.fire({
                          icon: 'success',
                          title: 'Priority Updated!',
                          text: `Set to ${selectedPriority}`,
                          toast: true,
                          position: 'top-end',
                          timer: 2000
                        });
                        refresh();
                      } catch (e) {
                        Swal.fire({ icon: 'error', title: 'Error', text: e.message, toast: true, position: 'top-end', timer: 2000 });
                      } finally {
                        setIsChangingPriority(false);
                      }
                    }}
                    className="ts-status-update-button"
                    style={{
                      marginTop: '10px',
                      padding: '6px 14px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                    disabled={isChangingPriority}
                  >
                    Update Priority
                  </button>
                )}
              </div>
            </div>
            )} 

            {hasPrivilege(userData, 'TICKET', 'TICKETS_ASSIGN') && (
              <div className="ts-status-selector-section">
                <h3 className="ts-section-title">
                  <User className="ts-icon-small ts-icon-margin-right" />
                  Assign To
                </h3>
                <div style={{ display: 'block', alignItems: 'center', gap: 8 }}>
                  <Select
                    className="ts-react-select-container"
                    classNamePrefix="ts-react-select"
                    options={users.map(u => ({ label: u.name, value: u.id }))}
                    isMulti
                    value={selectedAssignee}
                    onChange={setSelectedAssignee}
                    isDisabled={!hasPrivilege(userData, 'TICKET', 'TICKETS_ASSIGN')}
                    menuPortalTarget={document.body}
                    styles={{
                      placeholder: (provided) => ({
                        ...provided,
                        color: 'black',
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  <button
                    onClick={async () => {
                      setIsAssigning(true);
                      try {
                        await reassignTickets([ticket.id], selectedAssignee.map(a => a.value).join(','));
                        Swal.fire({
                          icon: 'success',
                          title: 'Assigned!',
                          text: `Now assigned to ${selectedAssignee.map(a => a.label).join(', ')}`,
                          toast: true, position: 'top-end', timer: 2000
                        });
                        refresh();
                      } catch (e) {
                        Swal.fire({ icon: 'error', title: 'Oops', text: e.message, toast: true, position: 'top-end', timer: 2000 });
                      } finally {
                        setIsAssigning(false);
                      }
                    }}
                    disabled={isAssigning || selectedAssignee.length === 0}
                    style={{
                      marginTop: '10px',
                      padding: '6px 14px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    {isAssigning ? '…' : 'Assign'}
                  </button>
                </div>
              </div>
            )}

            <div className="ts-activity-timeline-section">
              <h3 className="ts-section-title"><Clock className="ts-icon-small ts-icon-margin-right" /> Discussions ({combinedTimeline.length})</h3>
              <div className="ts-timeline-container">
                {combinedTimeline.length === 0 && <p className="ts-timeline-empty-text">No activity or comments yet.</p>}
                {timelineToShow.map((item, idx) => (
                  <div key={idx} className="ts-timeline-item">
                    <div className="ts-timeline-dot"></div>
                    {idx < timelineToShow.length - 1 && (
                      <div className="ts-timeline-line"></div>
                    )}
                    <div className="ts-timeline-content">
                      {item.type === 'comment' ? (
                        <div className="ts-timeline-card ts-timeline-card-comment">
                          <div className="ts-timeline-card-header">
                            <span className="ts-timeline-card-user-info">
                              <UserAvatar name={item.author} userId={userId} />
                              {item.author === userId ? 'You' : item.author} commented
                            </span>
                            <span className="ts-timeline-card-timestamp">{formatDate(item.timestamp)}
                              {hasPrivilege(userData, 'TICKET', 'COMMENT_DELETE') && (
                                <button
                                  className="ts-timeline-delete-btn"
                                  title="Delete Comment"
                                  onClick={() => handleDeleteComment(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#F87171",
                                    cursor: "pointer",
                                    marginLeft: 10,
                                    padding: 2,
                                    display: "inline-flex",
                                    alignItems: "center"
                                  }}
                                >
                                  <X size={16} />
                                </button>
                              )}
                            </span>
                          </div>
                          <p className="ts-timeline-card-text">
                            {item.text}
                          </p>
                          {item.attachments && item.attachments.length > 0 && (
                            <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {item.attachments.map((att, i) => {
                                if (att.photoUrl) {
                                  return (
                                    <img
                                      key={i}
                                      src={att.photoUrl}
                                      alt={`attachment-${i}`}
                                      style={{ width: '30%', height: '30%', objectFit: 'cover', borderRadius: 4, cursor: 'zoom-in' }}
                                      onClick={() => setMediaPreview({ type: 'image', url: att.photoUrl })}
                                    />
                                  );
                                }
                                if (att.videoUrl) {
                                  return (
                                    <video
                                      key={i}
                                      src={att.videoUrl}
                                      style={{ borderRadius: 4, background: '#eee', cursor: 'zoom-in', height: '50%', width: '50%' }}
                                      controls
                                      onClick={() => setMediaPreview({ type: 'video', url: att.videoUrl })}
                                    />
                                  );
                                }
                                return null;
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="ts-timeline-card ts-timeline-card-activity">
                          <div className="ts-timeline-card-header">
                            <span className="ts-timeline-card-user-info">
                              <UserAvatar name={item.user} userId={userId} />
                              {item.user === userId ? 'You' : item.user}
                            </span>
                            <span className="ts-timeline-card-timestamp">{formatDate(item.timestamp)}</span>
                          </div>
                          <p className="ts-timeline-card-text">
                            {item.details}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {combinedTimeline.length > 3 && (
                  <button
                    className="ts-timeline-showmore-btn"
                    onClick={() => setShowAllTimeline(v => !v)}
                    style={{
                      margin: "12px auto 0",
                      display: "block",
                      background: "#eee",
                      border: "none",
                      borderRadius: 6,
                      padding: "6px 18px",
                      cursor: "pointer",
                      fontWeight: 500,
                      fontSize: 13
                    }}
                  >
                    {showAllTimeline ? "Show less" : `Show more (${combinedTimeline.length - 3})`}
                  </button>
                )}
              </div>
            </div>

            {commentAttachments.length > 0 && (
              <div style={{ marginTop: 8, marginBottom: 8 }}>
                <strong>Preview:</strong>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {commentAttachments.map((file, idx) => (
                    <div key={idx} style={{ position: 'relative', width: 70, textAlign: 'center' }}>
                      {file.type.startsWith('image') ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`preview-${idx}`}
                          style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, display: 'block', margin: '0 auto', cursor: 'zoom-in' }}
                          onClick={() => setMediaPreview({ type: 'image', url: URL.createObjectURL(file) })}
                        />
                      ) : (
                        <video
                          src={URL.createObjectURL(file)}
                          width={100}
                          height={100}
                          style={{ borderRadius: 4, background: '#eee', display: 'block', margin: '0 auto', cursor: 'zoom-in' }}
                          controls
                          onClick={() => setMediaPreview({ type: 'video', url: URL.createObjectURL(file) })}
                        />
                      )}
                      <button
                        style={{
                          position: 'absolute', top: 2, right: 2, border: 'none', background: '#fff', borderRadius: '50%', cursor: 'pointer', padding: '0 4px', lineHeight: 1
                        }}
                        onClick={() => removeAttachment(idx)}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                      <div style={{ fontSize: 10, marginTop: 2, wordBreak: 'break-all' }}>{file.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasPrivilege(userData, 'TICKET', 'ADD_COMMENT') && (
              <div className="ts-comment-input-section" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="ts-comment-input"
                  onKeyPress={(e) => e.key === 'Enter' && addComment(ticket.id)}
                  style={{ flex: 1 }}
                />
                <button
                  className="ts-upload-icon-btn"
                  type="button"
                  title="Attach image or video"
                  onClick={() => document.getElementById('ts-comment-file-input').click()}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 6,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Paperclip className="ts-icon-medium" />
                  <span style={{ fontSize: 12, marginLeft: 3 }}></span>
                </button>
                <input
                  id="ts-comment-file-input"
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <button
                  onClick={() => addComment(ticket.id)}
                  className="ts-comment-send-button"
                  disabled={submitting}
                  style={submitting ? { opacity: 0.6, pointerEvents: 'none' } : {}}
                >
                  {submitting ? <span>...</span> : <Send className="ts-icon-small" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketDetail;
